// LaserArray - the shared rendering of a laser beam and of what it leaves on
// a surface, extracted verbatim from laser-cathedral.js so that
// laser-grid.js could reuse it rather than rewrite it. NOTHING here is new:
// every chunk below is the cathedral's own shader text with the handful of
// numbers that legitimately differ between consumers turned into factory
// arguments. The cathedral passes its historical values and renders
// byte-identically (verified by pixel diff against a seeded, deterministic
// capture); the grid passes its own.
//
// What a "laser" is, in this codebase, and why each piece is the way it is -
// all of it learned the hard way in laser-cathedral.js:
//
//  - WIDTH IS IN PIXELS, not world units. A world-sized quad collapses to a
//    couple of pixels at stage distance and the core profile then throws
//    most of that away; the vertex shader holds a minimum SCREEN width so a
//    beam stays a readable line at any depth (laserBillboard below).
//  - THE CORE IS NEAR-WHITE and a few pixels wide; it is what crosses the
//    bloom threshold and makes the line read as a laser. The coloured halo
//    alone is just tinted fog. How near-white is a consumer decision
//    (hotMix): the cathedral ships 0.85, the grid's brief asks 0.95.
//  - ALL INTENSITY LIVES IN THE COLOUR, alpha stays 1.0. AdditiveBlending is
//    (SRC_ALPHA, ONE): carrying intensity in both rgb and alpha squares it
//    and drives every beam to near nothing.
//  - A BEAM POINTING AT THE CAMERA degenerates twice: the billboard's side
//    vector vanishes (handled by laserPerp), and the quad seen down its own
//    axis becomes a screen-filling smear (handled by the guard, which dips
//    the gain near alignment but never to zero).

// Luminance ceiling, applied at the very end of every laser fragment shader.
// The interpolation is deliberately `${peak}` and not toFixed: the cathedral
// interpolated its PEAK_LUMINANCE constant raw, and the extracted text has to
// come out byte-identical for its pixel-diff to mean anything.
export function laserClampChunk(peakLuminance) {
  return `
  vec3 clampLuminance(vec3 col) {
    float mx = max(col.r, max(col.g, col.b));
    return mx > ${peakLuminance} ? col * (${peakLuminance} / mx) : col;
  }
`;
}

// The billboard: turns a point along a beam's centreline into a vertex of a
// camera-facing quad with a pixel-floored width, and reports the camera-axis
// guard for the consumer to fold into its gain.
//
// halfWidthNear/halfWidthFar: the beam's WORLD half-width at its two ends -
// real lasers diverge slightly, and that divergence is a large part of why
// they read as real.
export function laserBillboardChunk({ halfWidthNear, halfWidthFar }) {
  return `
  uniform float uPixelScale;  // pixels per world unit at unit view depth
  uniform float uOuterPx;     // the quad's minimum full width, in pixels

  // Any unit vector perpendicular to n, chosen so it never degenerates.
  // The cathedral's BEAM_DIR_CHUNK carries its own identical anyPerp(); this
  // one is named apart so both chunks can coexist in one shader without a
  // redefinition error.
  vec3 laserPerp(vec3 n) {
    vec3 a = abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    return normalize(cross(n, a));
  }

  // widthT: 0 at the source, 1 at the far end (drives the divergence).
  // sideSign: which edge of the quad this vertex is (-1 or +1, usually the
  // base geometry's position.x).
  vec3 laserBillboard(vec3 along, vec3 dir, float widthT, float sideSign, out float guard) {
    vec3 toCam = cameraPosition - along;
    float camDist = length(toCam);
    toCam = camDist > 0.0001 ? toCam / camDist : vec3(0.0, 0.0, 1.0);

    // Two separate concerns that must never be conflated into one factor
    // that could reach 0 and erase the beam entirely:
    //  - the SIDE vector genuinely degenerates when looking down the axis;
    //    laserPerp() already handles that.
    //  - the opacity should merely dip, never vanish.
    float align = abs(dot(dir, toCam));
    guard = mix(0.30, 1.0, 1.0 - smoothstep(0.90, 0.999, align));

    vec3 side = cross(dir, toCam);
    float sideLen = length(side);
    side = sideLen > 0.0001 ? side / sideLen : laserPerp(dir);

    // World-unit widths alone rendered ~5px at stage distance, most of which
    // the core profile then threw away. Hold a minimum SCREEN width instead,
    // so a beam stays a readable line at any depth.
    vec4 mv = modelViewMatrix * vec4(along, 1.0);
    float viewDepth = max(-mv.z, 0.001);
    float worldPerPx = viewDepth / uPixelScale;
    float halfWidth = max(mix(${halfWidthNear.toFixed(5)}, ${halfWidthFar.toFixed(5)}, widthT),
                          uOuterPx * 0.5 * worldPerPx);
    return along + side * (halfWidth * sideSign);
  }
`;
}

// The beam fragment: white core a few pixels wide, coloured halo, length
// falloff, dust sparkle, everything in rgb with alpha 1.
//
// fadeStart/fadeEnd (GLSL float literals, as STRINGS so the cathedral's
// default text stays byte-identical): where along the beam the length fade
// begins and dies. The cathedral's '0.5'/'1.0' lets its beams dissolve into
// the haze, which is right for light thrown at nothing; a beam that ENDS ON A
// SURFACE has to still be carrying light when it gets there, so laser-grid
// passes a later ramp and marks the arrival with its impact quad instead.
export function laserBeamFragmentShader({ hotMix, peakLuminance, fadeStart = '0.5', fadeEnd = '1.0' }) {
  return `
  precision mediump float;

  // uTime and uOuterPx are ALSO declared in the vertex shader, which runs at
  // highp. A uniform whose precision differs between the two stages fails to
  // link ("Precisions of uniform 'uTime' differ"), and the whole program is
  // silently dropped - which is exactly why the beams drew nothing while the
  // sheet and impacts, whose fragments never redeclare uTime, drew fine.
  // Qualified explicitly here so the fragment stage stays mediump by default
  // without breaking the match on these two.
  uniform highp float uTime;
  uniform highp float uOuterPx;
  uniform float uPulse;
  uniform float uCorePx;

  varying float vSide;
  varying float vLen;
  varying vec3 vColor;
  varying float vSeed;
  varying float vGain;
  varying float vSafeAtt;

  ${laserClampChunk(peakLuminance)}

  void main() {
    float u = abs(vSide);
    // Core expressed as a fraction of the quad's total screen width, so it
    // stays a few pixels wide at any distance.
    float coreW = uCorePx / uOuterPx;
    float core = 1.0 - smoothstep(0.0, coreW, u);
    float halo = (1.0 - u) * (1.0 - u);

    // Fades out along its length, as a real beam does through haze.
    float falloff = smoothstep(${fadeEnd}, ${fadeStart}, vLen);
    // Dust in the air.
    float sparkle = 0.92 + 0.08 * sin(uTime * 37.0 + vSeed * 19.0);

    // The near-white core is what crosses the bloom threshold (0.78) and makes
    // this read as a laser; the coloured halo alone is just tinted fog.
    vec3 hot = mix(vColor, vec3(1.0), ${hotMix.toFixed(2)});
    vec3 col = mix(vColor * halo * 0.55, hot, core);
    col *= falloff * sparkle * uPulse * vGain * vSafeAtt;

    // AdditiveBlending is (SRC_ALPHA, ONE), so the real contribution is
    // rgb * alpha. Carrying the intensity in BOTH squared it and drove every
    // beam to near nothing. All of it lives in col; alpha stays 1.
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;
}

// The impact fragment: what a beam leaves where it meets a surface - a small
// hard near-white dot inside a cubic coloured halo. The consumer's vertex
// shader owns WHERE and with what stretch; this only owns the profile.
export function laserImpactFragmentShader({ hotMix, peakLuminance }) {
  return `
  precision mediump float;

  uniform float uPulse;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${laserClampChunk(peakLuminance)}

  void main() {
    // vUv is the quad's own local coordinate, so the ellipse is already in
    // the stretched frame - distance here is uniform whatever the incidence.
    float r = length(vUv);
    float core = 1.0 - smoothstep(0.0, 0.16, r);
    float h1 = max(1.0 - r, 0.0);
    float halo = h1 * h1 * h1; // cubic, written as multiplies rather than pow()

    vec3 hot = mix(vColor, vec3(1.0), ${hotMix.toFixed(2)});
    vec3 col = mix(vColor * halo * 0.5, hot, core);
    col *= uPulse * vGain;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;
}

// The revealed-dust fragment: a soft round point whose whole visibility came
// from the vertex stage's reveal term (how close this particle sits to a
// beam). Generic; the reveal computation is the consumer's, because it
// depends entirely on how that consumer's beams are laid out.
export function laserDustFragmentShader({ brightness, peakLuminance }) {
  return `
  precision mediump float;

  varying float vReveal;
  varying vec3 vColor;

  ${laserClampChunk(peakLuminance)}

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float edge = (1.0 - d) * (1.0 - d);
    vec3 col = clampLuminance(vColor * vReveal * ${brightness.toFixed(2)});
    gl_FragColor = vec4(col, edge * vReveal);
  }
`;
}
